import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import {
  onAuthStateChanged,
  signInWithPopup,
  GoogleAuthProvider,
  signInWithEmailAndPassword,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  sendPasswordResetEmail,
  signOut as fb_sign_out,
  type User,
} from 'firebase/auth';
import { auth } from '../firebase';
import {
  exchange_session,
  logout as api_logout,
  set_unauthorized_handler,
  ApiError,
  type SessionInfo,
} from '../api';

export type AuthState =
  | { status: 'loading' }
  | { status: 'signed_out' }
  | { status: 'rejected'; reason: string }
  | { status: 'signed_in'; session: SessionInfo; email: string | null };

interface AuthContextValue {
  state: AuthState;
  sign_in_google: () => Promise<void>;
  sign_in_email: (email: string, password: string) => Promise<void>;
  send_sign_in_link: (email: string) => Promise<void>;
  send_password_reset: (email: string) => Promise<void>;
  sign_out: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// Where the email sign-in link returns to. Must be in Firebase Auth's authorized domains
// (console.captomi.com is). The SPA serves any path, so the app root handles the landing.
const EMAIL_LINK_REDIRECT = `${window.location.origin}/`;
const EMAIL_FOR_SIGN_IN = 'emailForSignIn'; // Firebase requires re-supplying the email on return

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: 'loading' });

  // If the page was opened from an email sign-in link, complete it. signInWithEmailLink lands a
  // verified-email credential → onAuthStateChanged fires → establish() exchanges it. Firebase
  // requires the email again on return (anti-tamper); we stashed it at send time, else prompt.
  useEffect(() => {
    if (!isSignInWithEmailLink(auth, window.location.href)) return;
    let email = window.localStorage.getItem(EMAIL_FOR_SIGN_IN);
    if (!email) email = window.prompt('Confirm the email this sign-in link was sent to') ?? '';
    if (!email) {
      setState({ status: 'rejected', reason: 'session_failed' });
      return;
    }
    signInWithEmailLink(auth, email, window.location.href)
      .then(() => {
        window.localStorage.removeItem(EMAIL_FOR_SIGN_IN);
        window.history.replaceState({}, '', window.location.pathname); // strip the link params
      })
      .catch(() => setState({ status: 'rejected', reason: 'session_failed' }));
  }, []);

  useEffect(
    () =>
      onAuthStateChanged(auth, (user) => {
        if (!user) {
          setState({ status: 'signed_out' });
          return;
        }
        void establish(user, setState);
      }),
    [],
  );

  // A 401 on a session-gated request (the session expired or was revoked mid-use) signs the
  // customer out → the shell shows Login again. F3 failure handling (docs/console.md Group F).
  useEffect(() => {
    set_unauthorized_handler(() => {
      void fb_sign_out(auth);
      setState({ status: 'signed_out' });
    });
    return () => set_unauthorized_handler(null);
  }, []);

  const value: AuthContextValue = {
    state,
    sign_in_google: async () => {
      await signInWithPopup(auth, new GoogleAuthProvider());
    },
    sign_in_email: async (email, password) => {
      await signInWithEmailAndPassword(auth, email, password);
    },
    send_sign_in_link: async (email) => {
      window.localStorage.setItem(EMAIL_FOR_SIGN_IN, email);
      await sendSignInLinkToEmail(auth, email, {
        url: EMAIL_LINK_REDIRECT,
        handleCodeInApp: true,
      });
    },
    // Standard "Forgot password" that doubles as first-time set: Firebase emails a link to its
    // hosted set-new-password page; completing it adds/updates the password credential (so a
    // Google-created account can gain a password). With email-enumeration protection on, this
    // resolves silently whether or not the account exists — the caller shows a generic message.
    send_password_reset: async (email) => {
      await sendPasswordResetEmail(auth, email);
    },
    sign_out: async () => {
      try {
        await api_logout();
      } catch {
        /* clear the client session regardless */
      }
      await fb_sign_out(auth);
      setState({ status: 'signed_out' });
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Firebase has authenticated the identity; now exchange it for a console session. A 403 means
// the identity matches no Captomi customer (no self-signup) → sign back out and surface it.
async function establish(user: User, setState: (s: AuthState) => void): Promise<void> {
  try {
    const id_token = await user.getIdToken();
    const session = await exchange_session(id_token);
    setState({ status: 'signed_in', session, email: user.email });
  } catch (err) {
    await fb_sign_out(auth);
    const reason = err instanceof ApiError && err.status === 403 ? err.reason : 'session_failed';
    setState({ status: 'rejected', reason });
  }
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}
